import React from 'react';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

type PredictiveData = {
    last30ATV: number;
    prev30ATV: number;
    atvChange: number;
    currentMonthRevenue: number;
    predictedMonthRevenue: number;
    currentMonthJobCount: number;
    predictedMonthJobCount: number;
    remainingDays: number;
    daysInMonth: number;
    peakHour: string;
    bestDay: string;
    bestDayRevenue: number;
    slowestDay: string;
    slowestDayRevenue: number;
    dayRevenue: number[]; // 7 elements, Sun-Sat
    dayCounts: number[]; // 7 elements, Sun-Sat
};

type PredictiveAnalyticsCardProps = {
    predictiveAnalytics: PredictiveData;
    loading?: boolean;
};

export const PredictiveAnalyticsCard: React.FC<PredictiveAnalyticsCardProps> = ({ predictiveAnalytics, loading }) => {
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');

    const pa = predictiveAnalytics || {} as PredictiveData;

    const last30ATV = pa.last30ATV || 0;
    const atvChange = pa.atvChange || 0;
    const currentMonthRevenue = pa.currentMonthRevenue || 0;
    const predictedMonthRevenue = pa.predictedMonthRevenue || 0;
    const currentMonthJobCount = pa.currentMonthJobCount || 0;
    const predictedMonthJobCount = pa.predictedMonthJobCount || 0;
    const remainingDays = pa.remainingDays || 0;
    const daysInMonth = pa.daysInMonth || 30;
    const peakHour = pa.peakHour || "N/A";
    const bestDay = pa.bestDay || "N/A";
    const bestDayRevenue = pa.bestDayRevenue || 0;
    const slowestDay = pa.slowestDay || "N/A";
    const slowestDayRevenue = pa.slowestDayRevenue || 0;
    const dayRevenue = pa.dayRevenue || new Array(7).fill(0);
    const dayCounts = pa.dayCounts || new Array(7).fill(0);

    // Derived values for recommendations
    const avgRevenuePerJob = last30ATV;
    const avgDailyRevenue = currentMonthRevenue / (daysInMonth - remainingDays || 1);
    const revenueGap = predictedMonthRevenue - currentMonthRevenue;
    const dailyJobsNeeded = last30ATV > 0 && remainingDays > 0 ? Math.ceil(revenueGap / last30ATV / remainingDays) : 0;
    const currentDailyAvg = (daysInMonth - remainingDays) > 0 ? currentMonthRevenue / (daysInMonth - remainingDays) : 0;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const bestDayIndex = dayNames.indexOf(bestDay);
    const slowestDayIndex = dayNames.indexOf(slowestDay);

    // Generate Smart Recommendations
    const generateSmartRecommendations = () => {
        const recommendations = [
            {
                icon: "bi-graph-up-arrow",
                color: "#50cd89",
                text: "Slow Day Growth Potential",
                subtext: `Boosting ${slowestDay} revenue by 30% could add ₹${Math.round((slowestDayRevenue * 0.3) * 4.3).toLocaleString()}/month. Try promotions or extended hours.`
            },
            {
                icon: "bi-calendar-check",
                color: "#009ef7",
                text: "Peak Day Leverage",
                subtext: `${bestDay}s generate ₹${Math.round(bestDayRevenue).toLocaleString()}. Replicate this success on other days for +${Math.round((bestDayRevenue - avgDailyRevenue) * 2).toLocaleString()}/month.`
            },
            {
                icon: "bi-cash-stack",
                color: "#ffc700",
                text: "Average Ticket Boost",
                subtext: `Current ATV: ₹${Math.round(last30ATV).toLocaleString()}. Increasing by 10% adds ₹${Math.round(last30ATV * 0.1 * predictedMonthJobCount).toLocaleString()} monthly revenue.`
            },
            {
                icon: "bi-speedometer2",
                color: "#f1416c",
                text: "Month-End Target",
                subtext: `Need ${dailyJobsNeeded} jobs/day for remaining ${remainingDays} days to hit ₹${predictedMonthRevenue.toLocaleString()} target.`
            },
            {
                icon: "bi-clock-fill",
                color: "#7239ea",
                text: "Peak Hour Maximization",
                subtext: `${peakHour} is your busiest window. Adding 1 express lane during this time could capture +₹${Math.round(avgRevenuePerJob * 2 * 30).toLocaleString()}/month.`
            },
            {
                icon: "bi-calendar2-week",
                color: "#00a3ff",
                text: "Weekend Opportunity",
                subtext: `Weekend revenue: ₹${Math.round((dayRevenue[0] + dayRevenue[6]) * 4.3).toLocaleString()}/month. Promote weekend services for +20% boost.`
            },
            {
                icon: "bi-lightning-charge",
                color: "#ff6b6b",
                text: "Daily Job Velocity",
                subtext: `Current: ${Math.round(currentMonthJobCount / (daysInMonth - remainingDays || 1))} jobs/day. Increase to ${Math.round(currentMonthJobCount / (daysInMonth - remainingDays || 1)) + 2} jobs/day = +₹${Math.round(avgRevenuePerJob * 2 * 30).toLocaleString()}/month.`
            },
            {
                icon: "bi-bar-chart-line",
                color: "#4ecdc4",
                text: "Revenue Consistency",
                subtext: `Best day is ${slowestDayRevenue > 0 ? Math.round((bestDayRevenue / slowestDayRevenue) * 100) : 0}% higher than slowest. Reducing gap by 25% = +₹${Math.round((bestDayRevenue - slowestDayRevenue) * 0.25 * 4.3).toLocaleString()}/month.`
            },
            {
                icon: "bi-plus-circle",
                color: "#95e1d3",
                text: "Treatment Upsell Impact",
                subtext: `If 30% of customers add 1 extra treatment (₹${Math.round(avgRevenuePerJob * 0.3).toLocaleString()}), monthly gain: ₹${Math.round(avgRevenuePerJob * 0.3 * predictedMonthJobCount * 0.3).toLocaleString()}.`
            },
            {
                icon: "bi-people-fill",
                color: "#a8e6cf",
                text: "Staffing Efficiency",
                subtext: `${bestDay}s handle ${bestDayIndex >= 0 ? dayCounts[bestDayIndex] : 0} jobs. Ensure ${Math.ceil((bestDayIndex >= 0 ? dayCounts[bestDayIndex] : 0) / 3)} staff on peak days for optimal flow.`
            },
            {
                icon: "bi-arrow-up-right-circle",
                color: "#ff8b94",
                text: "Growth Momentum",
                subtext: `ATV ${atvChange >= 0 ? 'up' : 'down'} ${Math.abs(atvChange).toFixed(1)}% vs last month. Maintain this trend for ₹${Math.round(currentMonthRevenue * (Math.abs(atvChange) / 100)).toLocaleString()}/month gain.`
            },
            {
                icon: "bi-hourglass-split",
                color: "#c7ceea",
                text: "Quiet Day Activation",
                subtext: `${slowestDay}s: Only ${slowestDayIndex >= 0 ? dayCounts[slowestDayIndex] : 0} jobs. Special offers could boost to ${Math.round((slowestDayIndex >= 0 ? dayCounts[slowestDayIndex] : 0) * 1.5)} jobs = +₹${Math.round(avgRevenuePerJob * (slowestDayIndex >= 0 ? dayCounts[slowestDayIndex] : 0) * 0.5 * 4.3).toLocaleString()}/month.`
            },
            {
                icon: "bi-percent",
                color: "#b8b8ff",
                text: "Conversion Opportunity",
                subtext: `Improving quote-to-job conversion by 15% could mean ${Math.round(predictedMonthJobCount * 0.15)} extra jobs = ₹${Math.round(avgRevenuePerJob * predictedMonthJobCount * 0.15).toLocaleString()}/month.`
            },
            {
                icon: "bi-smartwatch",
                color: "#ffd3b6",
                text: "Hourly Revenue Target",
                subtext: `Average ₹${Math.round(avgRevenuePerJob / 2).toLocaleString()}/hour. Optimize job flow to hit ₹${Math.round(avgRevenuePerJob / 2 * 1.2).toLocaleString()}/hour = +₹${Math.round(avgRevenuePerJob * 0.1 * predictedMonthJobCount).toLocaleString()}/month.`
            },
            {
                icon: "bi-grid-3x3-gap",
                color: "#ffaaa5",
                text: "Capacity Boost",
                subtext: `Best day: ${bestDayIndex >= 0 ? dayCounts[bestDayIndex] : 0} jobs. If all days hit 80% of this (${Math.round((bestDayIndex >= 0 ? dayCounts[bestDayIndex] : 0) * 0.8)}), monthly gain: ₹${Math.round(((bestDayIndex >= 0 ? dayCounts[bestDayIndex] : 0) * 0.8 * 30 - currentMonthJobCount) * avgRevenuePerJob).toLocaleString()}.`
            },
            {
                icon: "bi-star-fill",
                color: "#ff6f69",
                text: "Premium Mix Strategy",
                subtext: `Top 20% of jobs average ₹${Math.round(last30ATV * 1.8).toLocaleString()}. Moving 10% more jobs to premium tier = +₹${Math.round((last30ATV * 1.8 - last30ATV) * predictedMonthJobCount * 0.1).toLocaleString()}/month.`
            },
            {
                icon: "bi-bullseye",
                color: "#96ceb4",
                text: "Daily Revenue Target",
                subtext: `Hit ₹${Math.round(predictedMonthRevenue / daysInMonth).toLocaleString()}/day average for ₹${predictedMonthRevenue.toLocaleString()} month. Currently at ₹${Math.round(currentDailyAvg).toLocaleString()}/day.`
            },
            {
                icon: "bi-pie-chart-fill",
                color: "#dfe6e9",
                text: "Service Mix Balance",
                subtext: `High-value jobs (₹${Math.round(last30ATV * 1.5).toLocaleString()}+): ${Math.round(currentMonthJobCount * 0.2)} completed. Target ${Math.round(currentMonthJobCount * 0.3)} for +₹${Math.round(avgRevenuePerJob * 0.5 * currentMonthJobCount * 0.1).toLocaleString()}/month.`
            },
            {
                icon: "bi-graph-up",
                color: "#74b9ff",
                text: "Weekly Growth Rate",
                subtext: `Achieving 5% week-over-week growth compounds to +₹${Math.round(currentMonthRevenue * 0.05 * 4).toLocaleString()} by month end. Current pace: ${atvChange >= 0 ? '+' : ''}${atvChange.toFixed(1)}%.`
            },
            {
                icon: "bi-shield-check",
                color: "#a29bfe",
                text: "Baseline Revenue Stability",
                subtext: `Establish ₹${Math.round(slowestDayRevenue * 1.2).toLocaleString()} minimum daily revenue. This baseline adds ₹${Math.round((slowestDayRevenue * 1.2 - slowestDayRevenue) * 30).toLocaleString()}/month safety margin.`
            }
        ];

        // Randomly select 4 recommendations
        const shuffled = [...recommendations].sort(() => 0.5 - Math.random());
        return shuffled.slice(0, 4);
    };

    const smartRecommendations = React.useMemo(() => generateSmartRecommendations(), [predictiveAnalytics]);

    // --- UI Components ---

    const InsightBadge = ({ icon, color, text, subtext }: any) => (
        <div className={`d-flex align-items-center p-3 rounded mb-2`} style={{ backgroundColor: isDarkMode ? `${color}15` : `${color}10`, border: `1px solid ${color}30` }}>
            <div className="symbol symbol-40px me-3">
                <div className="symbol-label" style={{ backgroundColor: `${color}20` }}>
                    <i className={`bi ${icon} fs-2`} style={{ color: color }}></i>
                </div>
            </div>
            <div className="flex-grow-1">
                <div className={`fw-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{text}</div>
                <div className={`fs-7 ${isDarkMode ? 'text-white' : 'text-gray-600'}`} style={{ opacity: isDarkMode ? 0.85 : 1 }}>{subtext}</div>
            </div>
        </div>
    );

    const StatCard = ({ title, value, subtitle, trend, trendLabel, color }: any) => (
        <div className="card h-100 border-0 shadow-none" style={{ backgroundColor: isDarkMode ? '#151521' : '#f8f9fa' }}>
            <div className="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                    <div className={`fw-semibold fs-7 mb-1 text-uppercase ${isDarkMode ? 'text-white' : 'text-gray-500'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>{title}</div>
                    <div className={`fw-bolder fs-2x mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{value}</div>
                    <div className="d-flex align-items-center mb-2">
                        {trend !== undefined && (
                            <>
                                <i className={`bi ${trend >= 0 ? 'bi-arrow-up-short' : 'bi-arrow-down-short'} fs-4`}
                                   style={{ color: trend >= 0 ? '#50cd89' : '#f1416c' }}></i>
                                <span className="fw-bold fs-7 me-2" style={{ color: trend >= 0 ? '#50cd89' : '#f1416c' }}>
                                    {Math.abs(trend).toFixed(1)}%
                                </span>
                            </>
                        )}
                        <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>{trendLabel}</span>
                    </div>
                </div>
                {color && (
                    <div className={`h-4px w-100 rounded`} style={{ backgroundColor: `${color}20` }}>
                        <div className={`h-100 rounded`} style={{ width: '100%', backgroundColor: color }}></div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="card" style={{ backgroundColor: isDarkMode ? '#1e1e2d' : '#ffffff' }}>
            {/* Header */}
            <div className="card-header border-0 pt-6 pb-0">
                <h3 className={`card-title align-items-start flex-column`}>
                    <span className={`card-label fw-bolder fs-2 ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                        <i className="bi bi-lightbulb me-2 text-warning"></i>
                        Smart Business Insights
                    </span>
                    <span className={`mt-1 fw-bold fs-7 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.7 : 1 }}>AI-driven analysis of your service performance</span>
                </h3>
                <div className="card-toolbar">
                     <span className="badge badge-light-success fw-bolder px-4 py-2">
                        Updated Recently
                     </span>
                </div>
            </div>

            <div className="card-body py-6">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                        <span className="spinner-border text-primary"></span>
                    </div>
                ) : (
                    <div className="row g-5">

                        {/* Column 1: Financial Health (The "Money" View) */}
                        <div className="col-lg-4">
                            <h4 className={`fs-6 fw-bold mb-4 text-uppercase ls-1 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>Financial Pulse</h4>
                            <div className="d-flex flex-column gap-4">
                                {/* ATV Card */}
                                <div style={{ height: '140px' }}>
                                    <StatCard
                                        title="Avg. Ticket Value"
                                        value={`₹${Math.round(last30ATV).toLocaleString()}`}
                                        trend={atvChange}
                                        trendLabel="vs last 30 days"
                                        color="#009ef7"
                                    />
                                </div>
                                {/* Monthly Forecast Detailed Card */}
                                <div className="card border-0 shadow-none" style={{ backgroundColor: isDarkMode ? '#151521' : '#f8f9fa', minHeight: '200px' }}>
                                    <div className="card-body p-4">
                                        <div className={`fw-semibold fs-7 mb-3 text-uppercase ${isDarkMode ? 'text-white' : 'text-gray-500'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>
                                            Current Month Forecast
                                        </div>

                                        {/* Revenue Section */}
                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-gray-700'}`} style={{ opacity: isDarkMode ? 0.8 : 1 }}>Revenue</span>
                                                <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                                                    ₹{predictedMonthRevenue.toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="progress h-6px w-100 bg-light-success mb-1">
                                                <div className="progress-bar bg-success" role="progressbar"
                                                     style={{ width: `${predictedMonthRevenue > 0 ? (currentMonthRevenue / predictedMonthRevenue) * 100 : 0}%` }}></div>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.7 : 1 }}>Current: ₹{currentMonthRevenue.toLocaleString()}</span>
                                                <span className="fs-8 text-success fw-bold">Projected</span>
                                            </div>
                                        </div>

                                        {/* Jobs Section */}
                                        <div>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-gray-700'}`} style={{ opacity: isDarkMode ? 0.8 : 1 }}>Job Cards</span>
                                                <span className={`fs-6 fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`}>
                                                    {predictedMonthJobCount}
                                                </span>
                                            </div>
                                            <div className="progress h-6px w-100 bg-light-warning mb-1">
                                                <div className="progress-bar bg-warning" role="progressbar"
                                                     style={{ width: `${predictedMonthJobCount > 0 ? (currentMonthJobCount / predictedMonthJobCount) * 100 : 0}%` }}></div>
                                            </div>
                                            <div className="d-flex justify-content-between">
                                                <span className={`fs-8 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.7 : 1 }}>Current: {currentMonthJobCount}</span>
                                                <span className="fs-8 text-warning fw-bold">Projected</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Operational Patterns (The "Time" View) */}
                        <div className="col-lg-4">
                            <h4 className={`fs-6 fw-bold mb-3 text-uppercase ls-1 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>Traffic Patterns</h4>

                            {/* Compact Info Cards Row */}
                            <div className="row g-2 mb-3">
                                {/* Peak Performance - Compact */}
                                <div className="col-6">
                                    <div
                                        className="rounded-3 p-3 h-100"
                                        style={{
                                            background: isDarkMode
                                                ? 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
                                                : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                            border: isDarkMode ? '1px solid #4a5568' : '1px solid #d1d5db',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }}
                                    >
                                        <div className="text-center mb-2">
                                            <span className="fs-1">🔥</span>
                                        </div>
                                        <div className={`text-center fw-bold mb-1 ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>Peak Performance</div>
                                        <div className={`text-center ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '11px', opacity: 0.9, lineHeight: '1.4' }}>
                                            <div><strong>{bestDay}s</strong></div>
                                            <div className="mt-1">{peakHour}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quiet Times - Compact */}
                                <div className="col-6">
                                    <div
                                        className="rounded-3 p-3 h-100"
                                        style={{
                                            background: isDarkMode
                                                ? 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)'
                                                : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                                            border: isDarkMode ? '1px solid #4a5568' : '1px solid #d1d5db',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                        }}
                                    >
                                        <div className="text-center mb-2">
                                            <span className="fs-1">🐢</span>
                                        </div>
                                        <div className={`text-center fw-bold mb-1 ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>Quiet Times</div>
                                        <div className={`text-center ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ fontSize: '11px', opacity: 0.9, lineHeight: '1.4' }}>
                                            <div><strong>{slowestDay}s</strong></div>
                                            <div className="mt-1">Low activity</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Weekly Activity Chart - More Prominent */}
                            <div
                                className="rounded-3 p-3"
                                style={{
                                    background: isDarkMode ? '#1e1e2d' : '#f8f9fa',
                                    border: `1px solid ${isDarkMode ? '#2d2d3d' : '#e4e6ef'}`,
                                    marginTop: '20px'
                                }}
                            >
                                <div className="d-flex align-items-center mb-3">
                                    <i className="bi bi-bar-chart-fill me-2 text-primary fs-5"></i>
                                    <div>
                                        <div className={`fw-bold ${isDarkMode ? 'text-white' : 'text-dark'}`} style={{ fontSize: '13px' }}>
                                            Weekly Revenue
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced Visual Week Graph */}
                                <div className="d-flex align-items-end justify-content-between" style={{ height: '140px', paddingTop: '20px' }}>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => {
                                    const revenue = dayRevenue[i];
                                    const maxRevenue = Math.max(...dayRevenue);
                                    const heightPercentage = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                                    const barHeight = Math.max((heightPercentage * 0.8), revenue > 0 ? 20 : 8);
                                    const isBestDay = dayNames[i] === bestDay;
                                    const isSlowestDay = dayNames[i] === slowestDay;

                                    return (
                                        <div
                                            key={i}
                                            className="d-flex flex-column align-items-center"
                                            style={{ flex: 1, position: 'relative', gap: '12px' }}
                                        >
                                            {/* Bar with gradient and animation */}
                                            <div
                                                className="rounded"
                                                style={{
                                                    width: '100%',
                                                    maxWidth: '40px',
                                                    height: `${barHeight}px`,
                                                    background: isBestDay
                                                        ? 'linear-gradient(180deg, #50cd89 0%, #47be7d 100%)'
                                                        : isSlowestDay
                                                        ? 'linear-gradient(180deg, #f1416c 0%, #d63652 100%)'
                                                        : isDarkMode
                                                        ? 'linear-gradient(180deg, #4776E6 0%, #8E54E9 100%)'
                                                        : 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                                                    boxShadow: isBestDay || isSlowestDay
                                                        ? `0 4px 12px ${isBestDay ? 'rgba(80, 205, 137, 0.4)' : 'rgba(241, 65, 108, 0.4)'}`
                                                        : isDarkMode
                                                        ? '0 2px 8px rgba(71, 118, 230, 0.3)'
                                                        : '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    overflow: 'hidden'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                                                    e.currentTarget.style.boxShadow = isBestDay
                                                        ? '0 8px 20px rgba(80, 205, 137, 0.6)'
                                                        : isSlowestDay
                                                        ? '0 8px 20px rgba(241, 65, 108, 0.6)'
                                                        : isDarkMode
                                                        ? '0 6px 16px rgba(71, 118, 230, 0.5)'
                                                        : '0 6px 16px rgba(102, 126, 234, 0.5)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = isBestDay || isSlowestDay
                                                        ? `0 4px 12px ${isBestDay ? 'rgba(80, 205, 137, 0.4)' : 'rgba(241, 65, 108, 0.4)'}`
                                                        : isDarkMode
                                                        ? '0 2px 8px rgba(71, 118, 230, 0.3)'
                                                        : '0 2px 8px rgba(102, 126, 234, 0.3)';
                                                }}
                                                title={`${dayNames[i]}: ₹${Math.round(revenue).toLocaleString()}`}
                                            >
                                                {/* Shimmer effect */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                                    animation: `shimmer${i} 3s infinite`,
                                                }} />
                                            </div>

                                            {/* Day label with better styling */}
                                            <span
                                                className="fw-bold"
                                                style={{
                                                    fontSize: '11px',
                                                    color: isBestDay
                                                        ? '#50cd89'
                                                        : isSlowestDay
                                                        ? '#f1416c'
                                                        : isDarkMode
                                                        ? 'rgba(255, 255, 255, 0.7)'
                                                        : 'rgba(0, 0, 0, 0.6)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px'
                                                }}
                                            >
                                                {day}
                                            </span>

                                            {/* Best/Worst day indicator */}
                                            {(isBestDay || isSlowestDay) && (
                                                <div style={{
                                                    fontSize: '8px',
                                                    color: isBestDay ? '#50cd89' : '#f1416c',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {isBestDay ? '★' : '▼'}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                </div>
                            </div>

                            {/* Add shimmer animation keyframes */}
                            <style>{`
                                @keyframes shimmer0 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer1 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer2 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer3 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer4 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer5 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                                @keyframes shimmer6 { 0%, 100% { left: -100%; } 50% { left: 100%; } }
                            `}</style>
                        </div>

                        {/* Column 3: Actionable Insights (The "Business Owner" View) */}
                        <div className="col-lg-4">
                            <h4 className={`fs-6 fw-bold mb-4 text-uppercase ls-1 ${isDarkMode ? 'text-white' : 'text-muted'}`} style={{ opacity: isDarkMode ? 0.6 : 1 }}>
                                Smart Recommendations
                                <span className="ms-2" style={{ fontSize: '10px', opacity: 0.6 }}>(4 of 20)</span>
                            </h4>
                            <div className="d-flex flex-column gap-3">
                                {smartRecommendations.map((rec, idx) => (
                                    <InsightBadge
                                        key={idx}
                                        icon={rec.icon}
                                        color={rec.color}
                                        text={rec.text}
                                        subtext={rec.subtext}
                                    />
                                ))}
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};
